/**
 * The shot QA rule, in one place.
 *
 * A shot passes when gravity reads 1 g (the tool was the right way up and the
 * accelerometers agree) and the horizontal Earth rate matches what the survey's
 * latitude should produce (the tool was actually still). A moving tool smears
 * the gyro integration and WTotal drifts off the expected value.
 *
 * The tolerances deliberately mirror the Android app's OrientationCalculator
 * and the server port in Services/OrientationCalculator.cs, including the
 * strict inequalities: WTotal is a *proportional* tolerance, not a flat one.
 * The approval page previously used a flat +/-1.0, which is looser than the
 * tool's rule everywhere below ~48 degrees of latitude and tighter above it, so
 * the web page and the tool disagreed about the same shot. There should only
 * ever be one answer to "did this shot pass".
 *
 * HDD (DeviceType 2) is the exception. Those tools report the *total* Earth
 * rate rather than its horizontal component, so their WTotal sits at 15.041
 * deg/hr whatever the latitude. Checked against the latitude-derived horizontal
 * figure every HDD shot fails, so they get the total-field target and a flat
 * +/-0.5 deg/hr window instead.
 *
 * AQ tools with no bore-axis gyro (serials 4001 on) are the other exception.
 * Their azimuth is reconstructed from gravity and latitude on every shot, and
 * that reconstruction divides by cos(inclination), so it is undefined near
 * horizontal. Those shots fail QA whatever their GTotal and WTotal say.
 *
 * MiNS Memory is the third, and it changes every number here: a different Earth
 * rate, absolute rather than proportional bands, and a wider gravity window. It
 * is also always bore-gyro-less. Use surveyQaBands() rather than assembling the
 * pieces at a call site - the routing lives in one place, mirroring
 * Services/OrientationCalculator.cs.
 */

export const GTOTAL_TOLERANCE = 0.01;
export const WTOTAL_TOLERANCE_FRACTION = 0.1;

// Earth's rotation rate in deg/hr. The sidereal figure: 15.0 is the solar one
// and is 0.27% low. Matches EarthRateDegPerHour in the server's
// Services/OrientationCalculator.cs.
export const EARTH_RATE = 15.041;

// DeviceType 2 is HDD (0 = MiNS, 1 = Steering).
export const HDD_DEVICE_TYPE = 2;
export const HDD_WTOTAL = EARTH_RATE;
export const HDD_WTOTAL_TOLERANCE = 0.5;

// Below this the two-axis bore-rate solve amplifies noise without bound. The
// band is 73..107 degrees, symmetric about horizontal because the test is on
// |cos|, so 0-73 and 107-180 are both fine.
export const MIN_COS_INCLINATION = 0.30;

// MiNS Memory uses the solar Earth rate, not the sidereal one, and absolute QA
// bands rather than proportional ones. Not a rounding of the AQ figures: every
// deployed MiNS gyro scale factor was fitted against 15.0, so a viewer using
// 15.041 disagrees with Navigator on every MiNS shot. Mirrors
// Services/MinsOrientation.cs.
export const MINS_EARTH_RATE = 15.0;
export const MINS_WTOTAL_TOLERANCE = 1.0;
export const MINS_GTOTAL_TOLERANCE = 0.05;

export function isHddDeviceType(deviceType) {
    return deviceType === HDD_DEVICE_TYPE;
}

/**
 * True when this survey came off a MiNS Memory tool.
 *
 * From the calibration's shape, never from DeviceType - which is 0 for MiNS but
 * also 0 for AQ Gyro - and never from a serial. A MiNS tool carries a dedicated
 * two-axis GyroCalibration block and no Gyro3AxisCalibration. Note the AQ key is
 * present-but-null on every stored MiNS survey rather than absent, so this has
 * to test the value.
 *
 * Mirrors IsMinsCalibration in Services/MinsOrientation.cs.
 */
export function isMinsCalibration(survey) {
    const cal = survey && survey.CalibrationFile;
    return !!(cal && !cal.Gyro3AxisCalibration && cal.GyroCalibration);
}

/**
 * Every QA number a survey needs, chosen for its tool class in one place.
 *
 * @returns {{expectedWt: number, wTolerance: number, gTolerance: number, noBoreGyro: boolean}}
 *   noBoreGyro means the azimuth is reconstructed on every shot, so the
 *   inclination band applies: MiNS always, AQ only when its calibration says so.
 */
export function surveyQaBands(survey) {
    const latitude = survey && survey.Latitude;
    const deviceType = survey && survey.DeviceType;

    if (isMinsCalibration(survey)) {
        return {
            expectedWt: MINS_EARTH_RATE * Math.cos((latitude || 0) * Math.PI / 180),
            wTolerance: MINS_WTOTAL_TOLERANCE,
            gTolerance: MINS_GTOTAL_TOLERANCE,
            noBoreGyro: true
        };
    }

    const expectedWt = expectedWTotal(latitude, deviceType);
    return {
        expectedWt,
        wTolerance: wTotalTolerance(expectedWt, deviceType),
        gTolerance: GTOTAL_TOLERANCE,
        noBoreGyro: isTwoAxisCalibration(survey)
    };
}

// Expected Earth rate in deg/hr: the total field for HDD, otherwise the
// horizontal component at this latitude.
export function expectedWTotal(latitude, deviceType) {
    if (isHddDeviceType(deviceType)) return HDD_WTOTAL;
    return EARTH_RATE * Math.cos((latitude || 0) * Math.PI / 180);
}

/**
 * True when this survey's tool has no bore-axis gyro, identified by the four
 * Z-output coefficients its calibration pins when there is no Z signal to
 * describe. Exact equality on all four, and the keys must actually be present:
 * a calibration simply missing its Z block is a three-axis tool, not this.
 *
 * Mirrors IsTwoAxisCalibration in Services/OrientationCalculator.cs.
 */
export function isTwoAxisCalibration(survey) {
    const cal = survey && survey.CalibrationFile && survey.CalibrationFile.Gyro3AxisCalibration;
    if (!cal) return false;

    const pinned = { ZSF: 1, DZ: 0, MZX: 0, MZY: 0 };
    return Object.keys(pinned).every((key) => {
        const raw = cal[key];
        if (raw === undefined || raw === null) return false;
        // Coefficients arrive as single-element arrays; only the first is used.
        const value = Array.isArray(raw) ? raw[0] : raw;
        return value === pinned[key];
    });
}

/**
 * True when a shot's azimuth cannot be resolved at all: a two-axis tool too
 * close to horizontal for the reconstruction to have a defined answer.
 */
export function isAzimuthUnresolvable(twoAxis, inclination) {
    return !!twoAxis
        && Math.abs(Math.cos((inclination || 0) * Math.PI / 180)) < MIN_COS_INCLINATION;
}

// Flat window for HDD, proportional for everything else.
export function wTotalTolerance(expectedWt, deviceType) {
    if (isHddDeviceType(deviceType)) return HDD_WTOTAL_TOLERANCE;
    return expectedWt * WTOTAL_TOLERANCE_FRACTION;
}

/**
 * @param {{GTotal: number, WTotal: number}} reading - an OrientationSet, or any
 *   object exposing GTotal/WTotal (the re-source candidates share this shape).
 * @param {number} expectedGTotal - normally 1.0.
 * @param {number} expectedWt - from expectedWTotal(latitude, deviceType).
 * @param {number} [wTolerance] - from wTotalTolerance(); defaults to the
 *   proportional non-HDD window.
 * @param {boolean} [twoAxis] - noBoreGyro from surveyQaBands(). When set, the
 *   reading's Inclination must also sit outside the unresolvable band.
 * @param {number} [gTolerance] - from surveyQaBands(); defaults to the AQ window.
 *   MiNS's is five times wider.
 */
export function shotQaPass(reading, expectedGTotal, expectedWt, wTolerance = expectedWt * WTOTAL_TOLERANCE_FRACTION, twoAxis = false, gTolerance = GTOTAL_TOLERANCE) {
    if (!reading) return false;
    if (isAzimuthUnresolvable(twoAxis, reading.Inclination)) return false;
    // Strict, as the AQ server rule is. The MiNS server rule is inclusive, so the
    // two answer differently for a reading sitting exactly on a band edge - which
    // no measured double does.
    return reading.GTotal > expectedGTotal - gTolerance
        && reading.GTotal < expectedGTotal + gTolerance
        && reading.WTotal > expectedWt - wTolerance
        && reading.WTotal < expectedWt + wTolerance;
}
